<template>
  <input
    :placeholder="placeholder"
    class="input bg-white cursor-pointer w-full max-w-sm font-bold"
    v-bind="Object.assign({}, $attrs || {}, { value: selectedText })"
    type="text"
    v-model="selectedText"
    @keyup.prevent.stop
    @keypress.prevent.stop
    @keydown.prevent.stop
    @focus="open"
    @click="open"
  />
  <UIModal v-model="opened" :fixed="fixed" close close-text="Annuler">
    <div>
      <input
        ref="searchInput"
        :placeholder="searchPlaceholder"
        class="input bg-white"
        autocomplete="off"
        type="text"
        v-model="filter"
        @keyup.arrow-up.prevent="onUp"
        @keyup.arrow-down.prevent="onDown"
        @keyup.enter.prevent="onEnter"
      />
      <p v-if="loading" class="text-center pt-4 text-gray-700 text-sm">Chargement...</p>
      <p v-else-if="!filteredItems.length" class="text-center pt-4 text-gray-700 text-sm">
        <span v-if="filter.value">Rien trouvé</span>
      </p>
      <div v-else class="overflow-y-scroll my-2 border rounded-sm border-gray-200" style="max-height: 60vh">
        <ul class="block" :key="filteredItems.length">
          <li
            v-for="(item, index) in filteredItems"
            :key="`${index}${item.value}`"
            @click="selectedItem(item)"
            @mouseenter="hover = index"
            class="cursor-pointer select-none relative py-2 px-3"
            :class="{ 'bg-gray-200 text-gray-800': hover === index }"
          >
            <span :class="[selected === item ? 'font-semibold' : 'font-normal', 'block truncate']">
              {{ item.label || item.text }}
            </span>

            <span v-if="selected === item" class="text-indigo-600 absolute inset-y-0 right-0 flex items-center pr-4">
              <i class="fas fa-check"></i>
            </span>
          </li>
        </ul>
      </div>
    </div>
  </UIModal>
</template>

<script>
import UIModal from '@/components/UI/Atom/Modal'
import { computed, watch, ref, nextTick, onMounted } from 'vue'

/*
  clickOutside: Boolean,
  modelValue: Boolean,
  fullWidth: Boolean,
  close: Boolean,
  confirm: Boolean,
  closeText: String,
  closeClass: String,
  confirmClass: String,
*/
export default {
  props: {
    placeholder: {
      type: String,
      default: 'Selecteer ...',
    },
    searchPlaceholder: {
      type: String,
      default: 'Zoeken / Filteren ...',
    },
    modelValue: Object,
    items: {
      type: Array,
      default() {
        return []
      },
    },
    multiple: Boolean,
    fixed: Boolean,
    api: Function,
  },
  components: {
    UIModal,
  },
  emits: ['update:modelValue', 'selected'],
  setup(props, { emit }) {
    const loading = ref(false)
    const opened = ref(false)
    const filter = ref('')
    const searchInput = ref(null)
    const selected = ref(null)
    const hover = ref(-1)
    const innerItems = ref(props.items || [])
    const scroll = ref(0)
    const focus = () => {
      const input = searchInput.value
      if (!input) return
      if (input.focus) input.focus()

      const length = searchInput.value.value.length
      // Mostly for Web Browsers
      if (input.setSelectionRange) {
        input.setSelectionRange(length, length)
      } else if (input.createTextRange) {
        const t = input.createTextRange()
        t.collapse(true)
        t.moveEnd('character', length)
        t.moveStart('character', length)
        t.select()
      }
    }
    const open = async () => {
      filter.value = ''
      opened.value = true
      hover.value = -1
      scroll.value = window.scrollTop
      document?.activeElement?.blur()
      await nextTick()
      focus()
    }
    const filteredItems = computed(() => {
      const options = innerItems.value
      if (!filter.value || !options.length || typeof props.api === 'function') {
        return options
      }
      const term = String(filter.value).toLowerCase().replace(/\s+/gi, '')
      return options.filter(el =>
        Object.values(el || {})
          .join('__')
          .toLowerCase()
          .replace(/\s+/gi, '')
          .includes(term),
      )
    })
    const selectedText = computed(() => (selected.value && selected.value && selected.value.label) || '')
    let timeout = ref(0)
    const selectedItem = async item => {
      selected.value = item
      emit('update:modelValue', item)
      emit('selected', item)
      opened.value = false
      await nextTick()
      window.scrollTop = scroll.value
    }
    const handleChangeApi = () => {
      innerItems.value = []
      hover.value = -1
      clearTimeout(timeout.value)
      if (!filter.value) {
        return
      }
      loading.value = true
      timeout.value = setTimeout(() => {
        props
          .api(filter.value)
          .then(res => {
            innerItems.value = [].concat([], res?.data || [])
            if (innerItems.value.length) {
              hover.value = 0
            }
          })
          .finally(() => {
            loading.value = false
          })
      }, 1200)
    }
    onMounted(() => {
      if (props.modelValue) {
        const options = props.items
        const term = String(props.modelValue)
        const found = options.find(el => String(el.value) === term)
        if (found) {
          selectedItem(found)
        }
      }
      if (typeof props.api === 'function') {
        watch(() => filter.value, handleChangeApi)
      }
    })

    watch(
      () => filteredItems.value,
      val => {
        if (!val.length) hover.value = -1
      },
    )
    const onDown = () => {
      if (hover.value < 0) {
        hover.value = 0
      } else if (hover.value < filteredItems.value.length - 1) {
        hover.value += 1
      }
      focus()
    }
    const onUp = () => {
      if (hover.value < 0) {
        hover.value = filteredItems.value.length - 1
      } else if (hover.value > 0 && filteredItems.value.length) {
        hover.value -= 1
      }
      focus()
    }
    const onEnter = () => {
      if (hover.value >= 0 && hover.value <= filteredItems.value.length - 1) {
        selectedItem(filteredItems.value[hover.value])
      }
    }

    return {
      loading,
      selectedItem,
      selectedText,
      opened,
      searchInput,
      filter,
      open,
      filteredItems,
      selected,
      hover,
      onDown,
      onUp,
      onEnter,
    }
  },
}
</script>

<style></style>
